import * as z from "zod";
import {
  type CompositionToken,
  type IntentJudgement,
  sightOf,
  type SurfacingContext,
  WordText,
  type WordToken,
} from "@bindery/composition";

export const WORD_INTENT_PATH = "/api/compose/word-intent";

// A K–2 story runs to a few hundred words; past this the request is not a
// child's composition.
export const WORD_INTENT_MAX_WORDS = 500;
export const WORD_INTENT_MAX_WORD_LENGTH = 40;
// The plan's per-call cap on questions, a cost and latency choice (§ 8).
export const WORD_INTENT_MAX_JUDGED = 12;
export const WORD_INTENT_MAX_ALTERNATIVES = 3;

const TokenId = z.uuid();

const isUnique = (values: readonly string[]): boolean =>
  new Set(values).size === values.length;

export const wordIntentRequestSchema = z
  .object({
    words: z
      .array(
        z.object({
          id: TokenId,
          text: WordText.max(WORD_INTENT_MAX_WORD_LENGTH),
        }),
      )
      .min(1)
      .max(WORD_INTENT_MAX_WORDS)
      .refine((words) => isUnique(words.map((word) => word.id)), {
        message: "word ids must be unique",
      }),
    judge: z
      .array(TokenId)
      .min(1)
      .max(WORD_INTENT_MAX_JUDGED)
      .refine(isUnique, { message: "judged ids must be unique" }),
  })
  .refine(
    ({ words, judge }) => {
      const ids = new Set(words.map((word) => word.id));
      return judge.every((id) => ids.has(id));
    },
    { message: "every judged id must name one of the words", path: ["judge"] },
  );
export type WordIntentRequest = z.infer<typeof wordIntentRequestSchema>;

export const WordIntentOutcome = z.enum([
  "judged",
  "refused",
  "unjudged-timeout",
  "unjudged-unavailable",
]);
export type WordIntentOutcome = z.infer<typeof WordIntentOutcome>;

const Probability = z.number().min(0).max(1);

export const wordIntentSchema = z.object({
  tokenId: TokenId,
  // Whether the model answered this word's question; false when it refused
  // it or the call as a whole did not answer.
  judged: z.boolean(),
  intendedConfidence: Probability.nullable(),
  alternatives: z
    .array(z.object({ word: WordText, probability: Probability.nullable() }))
    .max(WORD_INTENT_MAX_ALTERNATIVES),
});
export type WordIntent = z.infer<typeof wordIntentSchema>;

// Every judged word comes back in every outcome: its alternatives are
// deterministic and serve the choose rung even when the model did not answer.
export const wordIntentJudgementsSchema = z.object({
  outcome: WordIntentOutcome,
  model: z.string().min(1).nullable(),
  words: z.array(wordIntentSchema),
});
export type WordIntentJudgements = z.infer<typeof wordIntentJudgementsSchema>;

// Whether each word sent, judged or not, is on the server's word list. The
// list is server-only (its licence), so this is the page's only source for
// the surfacing rule's non-dictionary signal. It needs no vendor, so it is
// answered in every outcome.
export const dictionaryEntrySchema = z.object({
  tokenId: TokenId,
  isDictionaryWord: z.boolean(),
});

export const wordIntentResponseSchema = wordIntentJudgementsSchema.extend({
  dictionary: z.array(dictionaryEntrySchema),
});
export type WordIntentResponse = z.infer<typeof wordIntentResponseSchema>;

export const wordIntentErrorSchema = z.object({
  error: z.enum([
    "unauthenticated",
    "invalid_request",
    "method_not_allowed",
    "unconfigured",
  ]),
});
export type WordIntentError = z.infer<typeof wordIntentErrorSchema>;

/** Token id → whether its text is a dictionary word, for every word sent. */
export const dictionaryOf = (
  response: WordIntentResponse,
): ReadonlyMap<string, boolean> =>
  new Map(
    response.dictionary.map((entry) => [entry.tokenId, entry.isDictionaryWord]),
  );

export interface TokenJudgement {
  readonly tokenId: string;
  readonly judgement: IntentJudgement;
}

/**
 * One `decisions-api` judgement per returned word, ready for a `judge` edit.
 * A word the model did not answer keeps its alternatives with a null model
 * and confidence, which raises no low-intent doubt when surfacing.
 */
export const toTokenJudgements = (
  response: WordIntentJudgements,
  at: number,
): TokenJudgement[] =>
  response.words.map((word) => ({
    tokenId: word.tokenId,
    judgement: {
      source: "decisions-api",
      model: word.judged ? response.model : null,
      intendedConfidence: word.intendedConfidence,
      alternatives: word.alternatives,
      at,
    },
  }));

const isLowSegment = (word: WordToken, context: SurfacingContext): boolean =>
  (word.asr?.segmentLogprob ?? 0) < context.thresholds.segmentLogprob;

const worthJudging = (word: WordToken, context: SurfacingContext): boolean =>
  word.resolution !== "confirmed" &&
  (word.origin === "dictated" || word.origin === "re-said") &&
  (sightOf(context.sight, word.text).status !== "sight" ||
    isLowSegment(word, context));

/**
 * The plan's rule for which words get a question (§ 4, The Decisions-API
 * intent check): every unconfirmed dictated word that is not a sight word,
 * plus any whose segment scored low, low-segment words first, at most
 * twelve. Null when nothing qualifies, so no call is made.
 */
export const wordIntentRequestFor = (
  tokens: readonly CompositionToken[],
  context: SurfacingContext,
): WordIntentRequest | null => {
  const words = tokens
    .flatMap((t) => (t.kind === "word" ? [t] : []))
    .slice(-WORD_INTENT_MAX_WORDS);
  const candidates = words.filter((word) => worthJudging(word, context));
  const judge = [
    ...candidates.filter((word) => isLowSegment(word, context)),
    ...candidates.filter((word) => !isLowSegment(word, context)),
  ]
    .slice(0, WORD_INTENT_MAX_JUDGED)
    .map((word) => word.id);
  return judge.length === 0
    ? null
    : { words: words.map(({ id, text }) => ({ id, text })), judge };
};
